import React, { useState, useEffect } from "react";
import { Sparkles, Loader2, X, Home } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Shown on every authenticated screen while the account is a no-signup "sandbox".
// Surfaces photo usage and converts the account (keeps all data) into a 15-day trial.
export function SandboxBanner() {
  const { user, convert } = useAuth();
  const [open, setOpen] = useState(false);
  const [a2hs, setA2hs] = useState(() => {
    try {
      const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator.standalone;
      if (standalone || localStorage.getItem("sp-installed") || localStorage.getItem("sp-a2hs-dismissed")) return false;
    } catch { /* ignore */ }
    return true;
  });

  // Let any cap-hit 402 elsewhere pop the convert modal (fired from api error handling / pages).
  useEffect(() => {
    const h = () => setOpen(true);
    window.addEventListener("sp:convert", h);
    return () => window.removeEventListener("sp:convert", h);
  }, []);

  if (user?.subscription?.status !== "sandbox") return null;
  const sb = user.sandbox || { photos: 0, photo_cap: 40 };
  const atCap = sb.photos >= sb.photo_cap;

  return (
    <>
      <div
        data-testid="sandbox-banner"
        className={`mx-3 mt-3 flex items-center gap-3 rounded-xl border px-3.5 py-2.5 md:mx-0 ${atCap ? "border-amber-500/50 bg-amber-500/10" : "border-emerald-500/40 bg-emerald-500/[0.08]"}`}
      >
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${atCap ? "bg-amber-500" : "bg-emerald-500"} text-white`}>
          <Sparkles className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold">
            {atCap ? "Free photo limit reached" : "You're on the free plan"}
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {atCap
              ? "Sign up free to keep adding proof"
              : sb.photos > 0
                ? `${sb.photos}/${sb.photo_cap} photos used · sign up to keep them`
                : "Sign up free to keep your lists & photos"}
          </p>
        </div>
        <Button
          size="sm"
          data-testid="sandbox-convert-btn"
          onClick={() => setOpen(true)}
          className="h-8 shrink-0 bg-emerald-500 px-3 text-xs font-bold hover:bg-emerald-600"
        >
          Keep it
        </Button>
      </div>
      {a2hs && (
        <button
          type="button"
          data-testid="sandbox-a2hs"
          onClick={() => window.dispatchEvent(new Event("sp:install-prompt"))}
          className="mx-3 mt-2 flex w-[calc(100%-1.5rem)] items-center gap-2 rounded-lg border border-border bg-card/60 px-3 py-2 text-left transition-colors hover:bg-card md:mx-0 md:w-full"
        >
          <Home className="h-4 w-4 shrink-0 text-emerald-500" />
          <span className="flex-1 truncate text-xs">
            <span className="font-semibold">Don't lose your list</span>
            <span className="text-muted-foreground"> — add to home screen</span>
          </span>
          <span
            data-testid="sandbox-a2hs-close"
            onClick={(e) => { e.stopPropagation(); try { localStorage.setItem("sp-a2hs-dismissed", "1"); } catch { /* ignore */ } setA2hs(false); }}
            className="shrink-0 rounded p-0.5 text-muted-foreground/70 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        </button>
      )}
      <ConvertModal open={open} onOpenChange={setOpen} convert={convert} defaultName={user.full_name || ""} />
    </>
  );
}

function ConvertModal({ open, onOpenChange, convert, defaultName }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState(defaultName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await convert(email, password, name);
      toast.success("Account created — your 15-day trial has started. Everything's saved.");
      onOpenChange(false);
      // Greet the brand-new member with the "Add to Home Screen" install prompt.
      setTimeout(() => window.dispatchEvent(new Event("sp:install-prompt")), 450);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Couldn't create your account. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="convert-modal">
        <DialogHeader>
          <DialogTitle>Save your list — free for 15 days</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">Add your email and your job, list and every photo stay saved — reopen them on any device. 15 days free, no card.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-3 space-y-3">
          <div>
            <label className="text-sm font-medium">Name</label>
            <Input data-testid="convert-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Cooper" className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <Input data-testid="convert-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 h-11" />
          </div>
          <div>
            <label className="text-sm font-medium">Password</label>
            <Input data-testid="convert-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="mt-1 h-11" />
          </div>
          {err && <p className="text-sm text-red-500" data-testid="convert-error">{err}</p>}
          <Button type="submit" disabled={busy} data-testid="convert-submit" className="h-11 w-full bg-emerald-500 text-base font-bold hover:bg-emerald-600">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save my list — free"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
